import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  MessageSquare, 
  Phone, 
  Check, 
  X, 
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  Settings,
  Send,
  MessageCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MessagingSettings() {
  const [status, setStatus] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const token = localStorage.getItem('token');
  
  const loadStatus = async () => {
    setLoading(true);
    try {
      const [statusRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/messaging/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/messaging/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (statusRes.ok) {
        setStatus(await statusRes.json());
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error('Error loading messaging status:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadStatus();
  }, []);
  
  const ConfigStatus = ({ configured, error, title }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <span className="font-medium">{title}</span>
      {configured ? (
        <Badge className="bg-green-100 text-green-800 flex items-center gap-1">
          <Check className="h-3 w-3" />
          Konfiguriert
        </Badge>
      ) : (
        <Badge className="bg-red-100 text-red-800 flex items-center gap-1">
          <X className="h-3 w-3" />
          Nicht konfiguriert
        </Badge>
      )}
    </div>
  );
  
  return (
    <div className="space-y-6" data-testid="messaging-settings-page">
      {/* Status Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              SMS & WhatsApp Konfiguration
            </CardTitle>
            <CardDescription>
              Twilio-Integration für automatische Benachrichtigungen
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={loadStatus} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="text-center py-4 text-gray-500">Lade Konfiguration...</div>
          ) : status ? (
            <>
              <ConfigStatus 
                configured={status.configured} 
                error={status.error}
                title="Twilio API"
              />
              <ConfigStatus 
                configured={status.sms?.configured} 
                error={status.sms?.error}
                title="SMS-Versand"
              />
              <ConfigStatus 
                configured={status.whatsapp?.configured} 
                error={status.whatsapp?.error}
                title="WhatsApp-Versand"
              />
              
              {status.sms_fallback_enabled && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="h-4 w-4 text-green-500" />
                  SMS-Fallback für WhatsApp aktiviert
                </div>
              )}
              
              {!status.configured && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="font-medium text-yellow-800 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Konfiguration erforderlich
                  </h4>
                  <p className="text-sm text-yellow-700 mt-2">
                    Um SMS und WhatsApp zu nutzen, müssen folgende Umgebungsvariablen gesetzt werden:
                  </p>
                  <ul className="text-sm text-yellow-700 mt-2 space-y-1 font-mono">
                    <li>• TWILIO_ACCOUNT_SID</li>
                    <li>• TWILIO_AUTH_TOKEN</li>
                    <li>• TWILIO_SMS_FROM</li>
                    <li>• TWILIO_WHATSAPP_FROM</li>
                  </ul>
                  <a 
                    href="https://console.twilio.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-yellow-800 hover:underline mt-3 inline-flex items-center gap-1"
                  >
                    Twilio Console öffnen
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
              
              {/* Webhook URLs */}
              {status.webhook_urls && (
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Webhook URLs für Twilio</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-blue-600">Status-Webhook:</span>
                      <code className="ml-2 bg-blue-100 px-2 py-0.5 rounded text-xs">
                        {window.location.origin}/api{status.webhook_urls.status}
                      </code>
                    </div>
                    <div>
                      <span className="text-blue-600">Inbound-Webhook:</span>
                      <code className="ml-2 bg-blue-100 px-2 py-0.5 rounded text-xs">
                        {window.location.origin}/api{status.webhook_urls.inbound}
                      </code>
                    </div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    Tragen Sie diese URLs in Ihrer Twilio-Konsole ein, um Status-Updates und eingehende Nachrichten zu empfangen.
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-4 text-red-500">
              Fehler beim Laden der Konfiguration
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Stats Card */}
      {stats && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Statistiken</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-gray-900">{stats.total_messages || 0}</div>
                <div className="text-sm text-gray-500">Gesamt</div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{stats.outbound || 0}</div>
                <div className="text-sm text-blue-500 flex items-center justify-center gap-1">
                  <Send className="h-3 w-3" />
                  Ausgehend
                </div>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-600">{stats.inbound || 0}</div>
                <div className="text-sm text-purple-500 flex items-center justify-center gap-1">
                  <MessageCircle className="h-3 w-3" />
                  Eingehend
                </div>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">
                  {stats.by_status?.delivered || 0}
                </div>
                <div className="text-sm text-green-500 flex items-center justify-center gap-1">
                  <Check className="h-3 w-3" />
                  Zugestellt
                </div>
              </div>
            </div>
            
            {/* By Channel */}
            {stats.by_channel && Object.keys(stats.by_channel).length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Nach Kanal</h4>
                <div className="flex gap-4">
                  {stats.by_channel.sms !== undefined && (
                    <Badge className="bg-blue-100 text-blue-800">
                      SMS: {stats.by_channel.sms}
                    </Badge>
                  )}
                  {stats.by_channel.whatsapp !== undefined && (
                    <Badge className="bg-green-100 text-green-800">
                      WhatsApp: {stats.by_channel.whatsapp}
                    </Badge>
                  )}
                </div>
              </div>
            )}
            
            {/* By Status */}
            {stats.by_status && Object.keys(stats.by_status).length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Nach Status</h4>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.by_status).map(([status, count]) => (
                    <Badge key={status} variant="outline" className="text-xs">
                      {status}: {count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Help Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Einrichtung</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-gray-600">
          <div>
            <h4 className="font-medium text-gray-800 mb-1">1. Twilio-Account erstellen</h4>
            <p>Registrieren Sie sich unter <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">twilio.com</a> und erhalten Sie Ihre Account-SID und Auth-Token.</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 mb-1">2. Telefonnummer kaufen</h4>
            <p>Kaufen Sie eine SMS-fähige Telefonnummer in der Twilio-Konsole.</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 mb-1">3. WhatsApp Business einrichten (optional)</h4>
            <p>Für produktive WhatsApp-Nachrichten müssen Sie den WhatsApp Business API-Prozess durchlaufen. Für Tests können Sie die Twilio Sandbox nutzen.</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 mb-1">4. Umgebungsvariablen setzen</h4>
            <p>Tragen Sie die Credentials in der .env-Datei ein und starten Sie den Server neu.</p>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 mb-1">5. Webhook URLs konfigurieren</h4>
            <p>Tragen Sie die oben angezeigten Webhook-URLs in Ihrer Twilio-Konsole ein, um Status-Updates zu empfangen.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
